import type { Icon } from '@phosphor-icons/react';

import { Label } from '@/components/ui/label';
import { RadioGroupItem } from '@/components/ui/radio-group';
import {
  SELECTABLE_CHOICE_CARD,
  SELECTABLE_CHOICE_CARD_OFF,
  SELECTABLE_CHOICE_CARD_ON,
} from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export interface RadioOptionCardProps {
  id: string;
  value: string;
  title: string;
  description: string;
  /** Restrained content-type icon for a goal or campaign-type card. */
  icon?: Icon;
  selected: boolean;
  /** Prevent selection while keeping the unavailable choice and its reason readable. */
  disabled?: boolean;
  /** Announced with the radio when it cannot be selected. */
  disabledReason?: string;
  className?: string;
}

/**
 * Card-sized radio option — the RadioGroup sibling of SELECTABLE_CHOICE_CARD.
 * Selection is carried by the SAME border-foreground/40 + bg-sidebar language
 * as every other selectable surface app-wide; the Radix radio indicator is
 * kept only for a11y (it announces the radio role/state to screen readers) —
 * the card treatment, not the dot, is what a sighted user reads as "selected".
 * The whole card is the hit target via the wrapping `<Label htmlFor>`.
 *
 * Source of truth for every RadioGroup-backed option-card chooser (Meta
 * campaign budget type, Google location options, and any future one) — do
 * not fork a third option-card treatment. Founder complaint this fixes:
 * the old hand-rolled cards "read as thin outlined boxes with a tiny raw
 * radio dot."
 */
export function RadioOptionCard({
  id,
  value,
  title,
  description,
  icon: Icon,
  selected,
  disabled = false,
  disabledReason,
  className,
}: RadioOptionCardProps): JSX.Element {
  const disabledReasonId = disabledReason ? `${id}-disabled-reason` : undefined;

  return (
    <Label
      htmlFor={id}
      spacing="none"
      className={cn(
        SELECTABLE_CHOICE_CARD,
        disabled
          ? 'cursor-not-allowed border-border/40 bg-muted/30 text-muted-foreground'
          : 'cursor-pointer flex-row items-start sm:flex-row sm:items-start',
        // Keyboard focus used to be carried by the visible radio dot. With the
        // dot hidden (below), the CARD has to show focus instead, or keyboard
        // users lose the ring entirely.
        'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring',
        !disabled && (selected ? SELECTABLE_CHOICE_CARD_ON : SELECTABLE_CHOICE_CARD_OFF),
        className,
      )}
    >
      {/*
        The dot is present for assistive tech and hidden from sight. This
        component's whole premise is that the CARD communicates selection —
        border-foreground/40 + bg-sidebar, the same grammar every selectable
        surface uses. Rendering a 20px radio dot beside that treatment states
        the same thing twice, and the second statement is the louder one:
        founder, 2026-08-12, on the Meta budget-type cards — "the radio button
        looks a little bit weird on this selected thing."
      */}
      <RadioGroupItem
        id={id}
        value={value}
        disabled={disabled}
        aria-describedby={disabledReasonId}
        className="sr-only"
      />
      <span className="flex h-full min-w-0 flex-1 self-stretch gap-3">
        {Icon ? (
          <Icon
            aria-hidden
            className="mt-0.5 size-4 shrink-0 text-muted-foreground"
            weight="regular"
          />
        ) : null}
        <span className="flex h-full min-w-0 flex-1 self-stretch flex-col space-y-0.5">
          <span className="block text-sm font-medium text-foreground">{title}</span>
          <span className="block text-caption font-normal text-muted-foreground">
            {description}
          </span>
          {disabledReason ? (
            <span
              id={disabledReasonId}
              className="mt-auto block pt-2 text-caption text-muted-foreground"
            >
              {disabledReason}
            </span>
          ) : null}
        </span>
      </span>
    </Label>
  );
}
